import { createFromRaw as createNotificationFromRaw } from 'data/notifications/record'
import { getNotificationSettings } from 'data/notifications/settings'
import {
  markNotificationsAsDisplayed,
  acknowledgeNotifications,
} from '../data/notifications/actions'
import { NOTIFICATION_JUST_CREATED } from 'data/notifications/actionTypes'
import { selectors as UsersSelectors } from 'data/users'
import * as NotificationsTypes from 'remote/notifications/types'

const makeNotifications = (settings, dispatch, notificationRaw) => {
  const notification = createNotificationFromRaw(notificationRaw)
  const { getTitle, getBody, navigate } = settings
  const hasNotificationApiSupport = window.Notification !== undefined

  if (!hasNotificationApiSupport) {
    return
  }

  function handleRequestPermissionResponse(result) {
    if (result !== 'granted') {
      return
    }
    const options = {
      body: getBody(notification.metadata),
    }
    const title = getTitle(notification.metadata)
    const nativeNotification = new window.Notification(title, options)
    nativeNotification.onclick = () => {
      window.focus()
      navigate(dispatch, notification)
      nativeNotification.close()
      dispatch(acknowledgeNotifications(({ id }) => id === notification.id))
    }
  }

  try {
    window.Notification.requestPermission().then(
      handleRequestPermissionResponse
    )
  } catch (error) {
    // Safari doesn't return a promise forafarequestPermissions and it throws a TypeError.
    // It takes a callback as the first argument instead.
    if (error instanceof TypeError) {
      window.Notification.requestPermission(handleRequestPermissionResponse)
    } else {
      throw error
    }
  }
}

const shouldAcknowledgeNotification = ({ getState }, notification) => {
  const user = UsersSelectors.getCurrentUser(getState())
  const noteId = notification.metadata.noteId
  if (!noteId) {
    return false
  }
  const userIdsOnNote = UsersSelectors.getUserIdsPresenceByNoteId(
    getState(),
    noteId
  )
  if (userIdsOnNote.has(user.id)) {
    if (
      [
        NotificationsTypes.NUDGE_ABOUT_NOTE,
        NotificationsTypes.MENTION_IN_NOTE,
      ].includes(notification.type)
    ) {
      // is the user on the note he is being nudged on
      return true
    }
    if (
      ([
        NotificationsTypes.ADD_THREAD_TO_NOTE,
        NotificationsTypes.MENTION_IN_COMMENT,
      ].includes(notification.type) &&
        window.location.pathname.endsWith('/comments')) || //is the user viewing the comment related to the notification
      (NotificationsTypes.REPLY_TO_COMMENT === notification.type &&
        window.location.pathname.endsWith('/comments') &&
        window.location.search.includes(notification.metadata.threadId)) //is the user viewing the thread panel relative to the comment of the notification
    ) {
      return true
    }
  }
  return false
}

const notificationMiddleware = ({ dispatch, getState }) => next => action => {
  const returnValue = next(action)
  if (action.type === NOTIFICATION_JUST_CREATED) {
    const notifications = action.payload
    const {
      displayedNotificationIds,
      acknowledgedNotificationIds,
    } = notifications.reduce(
      (data, notification) => {
        let displayedNotificationIds = data.displayedNotificationIds
        let acknowledgedNotificationIds = data.acknowledgedNotificationIds
        if (!notification.displayedAt) {
          const settings = getNotificationSettings(notification.type)
          if (settings) {
            makeNotifications(settings, dispatch, notification)
            displayedNotificationIds = displayedNotificationIds.concat(
              notification.id
            )
          }
        }
        if (shouldAcknowledgeNotification({ getState }, notification)) {
          acknowledgedNotificationIds = acknowledgedNotificationIds.concat(
            notification.id
          )
        }
        return { displayedNotificationIds, acknowledgedNotificationIds }
      },
      { displayedNotificationIds: [], acknowledgedNotificationIds: [] }
    )
    dispatch(markNotificationsAsDisplayed(displayedNotificationIds))
    dispatch(
      acknowledgeNotifications(({ id }) =>
        acknowledgedNotificationIds.includes(id)
      )
    )
  }
  return returnValue
}

export default notificationMiddleware
